import apiService from "@/services/apiService";
import { getReimbursementRequest } from "../admin/reimbursementRequestService";

const getDeliveryType = (pickupOption) => {
  switch (pickupOption) {
    case 0: return 'pickup_self';
    case 1: return 'pickup_friend';
    case 2: return 'mail';
    default: return 'pickup_self';
  }
};

const getPickupOption = (deliveryType) => {
  switch (deliveryType) {
    case 'pickup_self': return 0;
    case 'pickup_friend': return 1;
    case 'mail': return 2;
    default: return 0;
  }
};

const ReimbursementRequestService = {

  getChequeSettings: async () => {
    const response = await apiService.get('/reimbursement_requests/cheque_settings');
    return response.data;
  },

  transformToApiData: (inputData, clubId = null) => {
    const transformedData = {
      reimbursement_request: {
        reimbursement_description: inputData[0]?.reimbursementDescription || "",
        delivery_type: getDeliveryType(inputData[2]?.pickupOption || 0),
        event_present: !!inputData[0]?.selectedEvent,
        ...(clubId && { club_id: clubId }),
        event_form_id: inputData[0]?.selectedEvent || null,
        ...(inputData.draftId && {
          draft_id: inputData.draftId
        })
      }
    };

    if (inputData[1]?.expenses && inputData[1].expenses.length > 0) {
      transformedData.reimbursement_request.lines_attributes = inputData[1].expenses.map(expense => {
        const attachments = expense.attachments || [];
        const newSignedIds = attachments
          .filter(a => !a.attachment_id)
          .map(a => a.signed_id || a.blobId)
          .filter(Boolean);

        return {
          ...(expense.id && { id: expense.id }),
          expense_description: expense.description || "",
          amount: parseFloat(expense.price) || 0,
          ...(expense._destroy && { _destroy: true }),
          attachments_to_add: newSignedIds,
          attachments_to_remove: expense.attachments_to_remove || []
        };
      });
    }

    if (inputData[2]?.pickupOption === 1) {
      transformedData.reimbursement_request.authorized_pickup_attributes = {
        ...(inputData[2]?.authorizedPickupId && { id: inputData[2].authorizedPickupId }),
        first_name: inputData[2]?.firstName || "",
        last_name: inputData[2]?.lastName || "",
        email: inputData[2]?.email || "",
        phone_number: inputData[2]?.phone || ""
      };
    }

    if (inputData[2]?.pickupOption === 2 && inputData[2]?.selectedAddressId) {
      const address = inputData[2].selectedAddressId;
      transformedData.reimbursement_request.mailing_address_attributes = {
        ...(inputData[2]?.mailingAddressId && { id: inputData[2].mailingAddressId }),
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        address_line_1: address.address_line_1 || "",
        address_line_2: address.address_line_2 || "",
        city: address.city || "",
        region: address.region || "",
        country: address.country || "",
        postal_code: address.postal_code || "",
        phone_number: address.phone_number || ""
      };
    }

    return transformedData;
  },
  transformFromApiData: (rawData) => {
    const isDraft = !!rawData.draft;
    const data = rawData.draft || rawData;

    return [
      {
        reimbursementDescription: data.reimbursement_description || "",
        chequeMadePayableTo: data.cheque_payable_name || "",
        selectedEvent: data.event_form?.id || null,
        eventPresent: !!data.event_form?.id
      },
      {
        expenses: (data.lines || []).map(line => ({
          id: line.id || null,
          description: line.expense_description || "",
          price: parseFloat(line.amount) || 0,
          attachments: (line.supporting_documents || []).map(doc => ({
            attachment_id: doc.attachment_id || null,
            blobId: doc.blob_id || doc.signed_id,
            signed_id: doc.signed_id,
            url: doc.url,
            display_url: doc.display_url,
            name: doc.filename || 'Document',
            type: doc.content_type,
            size: doc.byte_size || 0,
            preview_url: doc.preview_url
          }))
        }))
      },
      {
        pickupOption: getPickupOption(data.delivery_type),
        authorizedPickupId: isDraft ? null : (data.authorized_pickup?.id || null),
        mailingAddressId: isDraft ? null : (data.mailing_address?.id || null),
        firstName: data.authorized_pickup?.first_name || "",
        lastName: data.authorized_pickup?.last_name || "",
        email: data.authorized_pickup?.email || "",
        phone: data.authorized_pickup?.phone_number || "",
        authorizedPerson: data.authorized_pickup ? {
          firstName: data.authorized_pickup.first_name || "",
          lastName: data.authorized_pickup.last_name || "",
          email: data.authorized_pickup.email || "",
          phone: data.authorized_pickup.phone_number || ""
        } : {},
        selectedAddressId: data.mailing_address ? {
          first_name: data.mailing_address.first_name || "",
          last_name: data.mailing_address.last_name || "",
          address_line_1: data.mailing_address.address_line_1 || "",
          address_line_2: data.mailing_address.address_line_2 || "",
          city: data.mailing_address.city || "",
          region: data.mailing_address.region || "",
          country: data.mailing_address.country || "",
          postal_code: data.mailing_address.postal_code || "",
          phone_number: data.mailing_address.phone_number || ""
        } : null
      }
    ];
  },

  getClub: async (clubId) => {
    if (!clubId) {
      throw new Error('Club ID is required');
    }

    const response = await apiService.get(`/clubs/${clubId}/profile`);
    return response.data;
  },

  checkClubEligible: async (clubId) => {
    const response = await apiService.get('/reimbursement_requests/club_eligible', { params: { club_id: clubId } });
    return response.data;
  },
  
  saveDraft: async (data) => {
    const response = await apiService.post('/reimbursement_requests/save_draft', data);
    return response.data;
  },

  restoreDraft: async (id) => {
    const response = await apiService.get(`/reimbursement_requests/restore_draft/${id}`);
    return response.data;
  },

  deleteDraft: async (draftId) => {
    const response = await apiService.delete(`/reimbursement_requests/drafts/${draftId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await apiService.post('/reimbursement_requests', data);
    return response.data;
  },

  submitReimbursementRequest: async (requestId) => {
    const response = await apiService.patch(`/reimbursement_requests/${requestId}/submit`);
    return response.data;
  },

  getReimbursementRequest: async (requestId) => {
    if (!requestId) {
      throw new Error('getReimbursementRequest: requestId is required');
    }
    const response = await apiService.get(`/reimbursement_requests/${requestId}`);
    return response.data;
  },

  refreshBalance: async (requestId) => {
    const response = await apiService.post(`/reimbursement_requests/${requestId}/refresh_balance`);
    return response.data;
  },

  updateReimbursementRequest: async (requestId, data) => {
    const response = await apiService.patch(`/reimbursement_requests/${requestId}`, data);
    return response.data;
  },

  withdrawReimbursementRequest: async (requestId) => {
    const response = await apiService.patch(`/reimbursement_requests/${requestId}/withdraw`);
    return response.data;
  },

  signingAuthorityApprove: async (requestId) => {
    const response = await apiService.patch(`/reimbursement_requests/${requestId}/signing_authority_approve`);
    return response.data;
  },

  signingAuthorityReject: async (requestId, comment) => {
    const response = await apiService.patch(`/reimbursement_requests/${requestId}/signing_authority_reject`, { comment });
    return response.data;
  },

  sendBack: async (requestId, comment) => {
    const response = await apiService.patch(`/reimbursement_requests/${requestId}/send_back`, { comment });
    return response.data;
  },

  transformApiDataForDisplay: (apiData) => {
    const request = apiData.reimbursement_request;
    const transformedData = ReimbursementRequestService.transformFromApiData(request);
    
    let data = {
      clubName: request.club?.name,
      clubAcronym: request.club?.acronym,
      name: request.submitted_by?.name,
      email: request.submitted_by?.email,
      description: transformedData[0].reimbursementDescription,
      payableTo: transformedData[0].chequeMadePayableTo,
      payableToModified: !!request.cheque_payable_name_modified,
      requestDate: request.requested_date ? new Date(request.requested_date) : new Date(),
      eventObj: request.event_form || null,
      eventPresent: request.event_present || false,
      expenses: transformedData[1].expenses,
      pickup: transformedData[2].pickupOption,
      authorizedPerson: transformedData[2].authorizedPerson,
      mailedAddress: transformedData[2].selectedAddressId,
      user: {
        fullname: request.submitted_by?.name || '',
        email: request.submitted_by?.email || ''
      },
      aasmState: request.aasm_state,
      requestHistories: request.request_histories || [],
      requestedDate: request.requested_date || null,
      requested_date_fmt: request.requested_date_fmt || null,
      formId: request.form_id,
      id: request.id,
      slug: request.slug,
      policies: apiData.policies || [],
      signing_authority_widget: apiData.signing_authority_widget || null,
      status_text: request.status_text || '',
      ticket_id: request.ticket_id,
      created_at: request.created_at || null,
      created_at_fmt: request.created_at_fmt || null,
      updated_at: request.updated_at || null,
      updated_at_fmt: request.updated_at_fmt || null,
      submitted_by: request.submitted_by ? {
        name: request.submitted_by.name || '',
        email: request.submitted_by.email || ''
      } : null,
      updated_by: request.updated_by ? {
        name: request.updated_by.name || '',
        email: request.updated_by.email || ''
      } : null,
      club: request.club ? {
        // Backend serializes the nested club as { club_id, name, acronym }
        // (see ReimbursementRequestHelper#format_reimbursement_request).
        id: request.club.club_id ?? request.club.id,
        name: request.club.name
      } : null,
      balance: apiData.balance ??  null,
      balanceUpdatedAt: apiData.balance_updated_at ?? null,
      clubCode: apiData.club_code ?? null,
      batchName: request.batch_name || null
    };

    if (apiData.wusa_signing_authorities) {
      data.wusa_signing_authorities = apiData.wusa_signing_authorities;
    }

    if (apiData.send_back_label) {
      data.sendBackLabel = apiData.send_back_label;
    }

    if (apiData.previous_state) {
      data.previousState = apiData.previous_state;
    }

    if (apiData.hold_details) {

      data.hold_details = apiData.hold_details;
    }

    return data;
  },

  getReimbursementRequests: async (clubId, params) => {
    if (!clubId) {
      throw new Error('Club ID is required');
    }

    const requestParams = {
      club_id: clubId,
      page: params.page || 1,
      limit: params.limit || 10,
      sort_field: params.sort_field || 'requested_date',
      sort_order: params.sort_order || 'desc'
    };

    if (params.q && typeof params.q === 'object' && Object.keys(params.q).length > 0) {
      Object.keys(params.q).forEach(key => {
        if (params.q[key] && params.q[key].trim() !== '') {
          requestParams[`q[${key}]`] = params.q[key];
        }
      });
    } else {
      requestParams['q[description]'] = '';
    }
    const response = await apiService.get('/reimbursement_requests', { params: requestParams });

    return response.data;
  },

  getReimbursementRequestsForUser: async (params) => {
      const requestParams = {
          page: params.page || 1,
          limit: params.limit || 10,
          sort_field: params.sort_field || 'requested_date',
          sort_order: params.sort_order || 'desc'
      };

      if (params.q && typeof params.q === 'object' && Object.keys(params.q).length > 0) {
          Object.keys(params.q).forEach(key => {
              if (params.q[key] && params.q[key].trim() !== '') {
                  requestParams[`q[${key}]`] = params.q[key];
              }
          });
      } else {
          requestParams['q[description]'] = '';
      }
      const response = await apiService.get('/reimbursement_requests/user_requests', { params: requestParams });

      return response.data;
  },

  getAllReimbursementRequestsForUser: async (params) => {
      const requestParams = {
          page: params.page || 1,
          limit: params.limit || 10,
          sort_field: params.sort_field || 'requested_date',
          sort_order: params.sort_order || 'desc'
      };

      if (params.q && typeof params.q === 'object' && Object.keys(params.q).length > 0) {
          Object.keys(params.q).forEach(key => {
              if (params.q[key] && params.q[key].trim() !== '') {
                  requestParams[`q[${key}]`] = params.q[key];
              }
          });
      } else {
          requestParams['q[description]'] = '';
      }
      const response = await apiService.get('/reimbursement_requests/all_user_requests', { params: requestParams });

      return response.data;
  },

  getReimbursementRequestPendingSigningAuthorityApproval: async (clubId, params) => {
    if (!clubId) {
      throw new Error('Club ID is required');
    }

    const requestParams = {
      club_id: clubId,
      page: params.page || 1,
      limit: params.limit || 10,
      sort_field: params.sort_field || 'requested_date',
      sort_order: params.sort_order || 'desc'
    };

    if (params.q && typeof params.q === 'object' && Object.keys(params.q).length > 0) {
      Object.keys(params.q).forEach(key => {
        if (params.q[key] && params.q[key].trim() !== '') {
          requestParams[`q[${key}]`] = params.q[key];
        }
      });
    } else {
      requestParams['q[description]'] = '';
    }
    const response = await apiService.get('/reimbursement_requests/pending_signing_auth', { params: requestParams });

    return response.data;
  },

  getCompletedRequests: async (clubId, params) => {
    if (!clubId) {
      throw new Error('Club ID is required');
    }
    
    const requestParams = {
      club_id: clubId,
      page: params.page || 1,
      limit: params.limit || 10,
      sort_field: params.sort_field || 'requested_date',
      sort_order: params.sort_order || 'desc'
    };
    if (params.q && typeof params.q === 'object' && Object.keys(params.q).length > 0) {
      Object.keys(params.q).forEach(key => {
        if (params.q[key] && params.q[key].trim() !== '') {
          requestParams[`q[${key}]`] = params.q[key];
        }
      });
    } else {
      requestParams['q[description]'] = '';
    }
    const response = await apiService.get('/reimbursement_requests/completed_requests', { params: requestParams });
    
    return response.data;
  },

  getOpenRequestsForClub: async (clubId, params) => {
    if (!clubId) {
      throw new Error('Club ID is required');
    }
    
    const requestParams = {
      club_id: clubId,
      page: params.page || 1,
      limit: params.limit || 10,
      sort_field: params.sort_field || 'requested_date',
      sort_order: params.sort_order || 'desc'
    };
    if (params.q && typeof params.q === 'object' && Object.keys(params.q).length > 0) {
      Object.keys(params.q).forEach(key => {
        if (params.q[key] && params.q[key].trim() !== '') {
          requestParams[`q[${key}]`] = params.q[key];
        }
      });
    } else {
      requestParams['q[description]'] = '';
    }
    const response = await apiService.get('/reimbursement_requests/open_requests', { params: requestParams });
    
    return response.data;
  },

  downloadArchive: async (requestId) => {
    if (!requestId) {
      throw new Error('Request ID is required');
    }

    const response = await apiService.get(`/reimbursement_requests/${requestId}/download_archive`, {
      responseType: 'blob'
    });

    // Extract filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `cheque_request_${requestId}.zip`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    return {
      blob: response.data,
      filename,
      contentType: 'application/zip'
    };
  }

}

export default ReimbursementRequestService;